import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import MobileInvoicePreview from './MobileInvoicePreview';
import EmailVerificationBanner from '../common/EmailVerificationBanner';
import StripeConnectBanner from '../common/StripeConnectBanner';
import TwoFactorReminderBanner from '../common/TwoFactorReminderBanner';
import {
  FileText,
  CreditCard,
  MessageSquare,
  TrendingUp,
  Plus,
  ArrowRight,
  Clock,
  CheckCircle,
  AlertCircle,
  PoundSterling,
  Users,
  Calendar
} from 'lucide-react';
import api from '../../utils/api';
import { downloadInvoice } from '../../utils/invoiceDownload';

const MobileClientDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showInvoicePreview, setShowInvoicePreview] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const handleViewInvoice = async (invoice) => {
    try {
      // Fetch full invoice details including line items
      const response = await api.get(`/invoices/${invoice.id}`);
      const fullInvoice = response.data.invoice || response.data;
      
      setSelectedInvoice({
        ...invoice,
        ...fullInvoice,
        lineItems: fullInvoice.items || fullInvoice.lineItems || invoice.lineItems || []
      });
      setShowInvoicePreview(true);
    } catch (error) {
      console.error('Error fetching invoice details:', error);
      // Fallback to basic invoice data
      setSelectedInvoice(invoice);
      setShowInvoicePreview(true);
    }
  };

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/api/dashboard/client-summary?period=monthly');
      console.log('Mobile Dashboard - Full API Response:', JSON.stringify(response.data, null, 2));
      console.log('Mobile Dashboard - Response.data.data:', response.data.data);
      console.log('Mobile Dashboard - KPIs (response.data.kpis):', response.data.kpis);
      console.log('Mobile Dashboard - KPIs (response.data.data.kpis):', response.data.data?.kpis);
      console.log('Mobile Dashboard - Next Payment Due (raw):', response.data.data?.kpis?.nextPaymentDue);
      console.log('Mobile Dashboard - Next Payment Due (stringified):', JSON.stringify(response.data.data?.kpis?.nextPaymentDue));
      console.log('Mobile Dashboard - Next Payment Due Date:', response.data.data?.kpis?.nextPaymentDue?.date);
      console.log('Mobile Dashboard - Next Payment Due Date Type:', typeof response.data.data?.kpis?.nextPaymentDue?.date);
      console.log('Mobile Dashboard - Next Payment Due Amount:', response.data.data?.kpis?.nextPaymentDue?.amount);
      
      // Unwrap the data if needed
      const dashboardResponse = response.data.data || response.data;
      console.log('Mobile Dashboard - Dashboard Response (after unwrap):', JSON.stringify(dashboardResponse, null, 2));
      console.log('Mobile Dashboard - Dashboard Response KPIs:', dashboardResponse.kpis);
      console.log('Mobile Dashboard - Dashboard Response Next Payment Due:', dashboardResponse.kpis?.nextPaymentDue);
      if (response.data.success) {
        // Unwrap the data field if it exists
        const dashboardResponse = response.data.data || response.data;
        setDashboardData(dashboardResponse);
      } else {
        setError('Failed to fetch dashboard data');
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      console.error('Error details:', error.response?.data);
      setError(error.response?.data?.message || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };
  const getActivityDestination = (activity = {}) => {
    const invoiceIdentifier = activity.invoiceId || activity.id || null;
    const invoiceNumber = activity.invoiceNumber || activity.invoice_no || (activity.description ? activity.description.match(/Invoice\s([A-Za-z0-9-]+)/)?.[1] : null);
    const type = activity.type || '';
    const paymentTypes = ['payment_received', 'payment_made'];
    const sentTypes = ['invoice_sent', 'invoice_rejected', 'invoice_created', 'invoice_updated'];

    if (paymentTypes.includes(type)) {
      return {
        path: '/portal/payments',
        highlight: invoiceIdentifier
          ? String(invoiceIdentifier)
          : invoiceNumber
            ? `number:${invoiceNumber}`
            : null
      };
    }

    const tab = sentTypes.includes(type) ? 'sent' : 'received';
    return {
      path: '/portal/invoices',
      tab,
      highlight: invoiceIdentifier
        ? String(invoiceIdentifier)
        : invoiceNumber
          ? `number:${invoiceNumber}`
          : null
    };
  };

  const handleActivityNavigation = (activity) => {
    const { path, tab, highlight } = getActivityDestination(activity);
    if (!path) {
      return;
    }

    const params = new URLSearchParams();
    if (highlight) {
      params.set('highlight', highlight);
    }
    if (tab && path.includes('/portal/invoices')) {
      params.set('tab', tab);
    }

    const destination = params.toString() ? `${path}?${params.toString()}` : path;
    navigate(destination);
  };

  const QuickActionCard = ({ icon: Icon, title, description, href, color = 'blue' }) => (
    <Link to={href} className="block">
      <motion.div
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        className={`bg-gradient-to-br from-${color}-50 to-${color}-100 p-4 rounded-xl border border-${color}-200`}
      >
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <div className={`w-10 h-10 bg-${color}-500 rounded-lg flex items-center justify-center mb-3`}>
              <Icon className="w-5 h-5 text-white" />
            </div>
            <h3 className="font-semibold text-gray-900 mb-1">{title}</h3>
            <p className="text-sm text-gray-600">{description}</p>
          </div>
          <ArrowRight className="w-5 h-5 text-gray-400" />
        </div>
      </motion.div>
    </Link>
  );

  const StatCard = ({ title, value, icon: Icon, color = 'blue', change, period, changeType = 'percentage', onClick }) => (
    <motion.div
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      onClick={onClick}
      className={`bg-gradient-to-br from-${color}-50 to-${color}-100 p-4 rounded-xl border border-${color}-200 ${onClick ? 'cursor-pointer active:scale-95' : ''}`}
    >
      <div className="flex items-center justify-between mb-2">
        <div className={`w-8 h-8 bg-${color}-500 rounded-lg flex items-center justify-center`}>
          <Icon className="w-4 h-4 text-white" />
        </div>
        {change !== undefined && change !== 0 && (
          <span className={`text-xs font-medium ${change > 0 ? 'text-green-600' : 'text-red-600'}`}>
            {change > 0 ? '+' : ''}{change}{changeType === 'percentage' ? '%' : ''}
          </span>
        )}
      </div>
      <h3 className="text-2xl font-bold text-gray-900 mb-1">{value}</h3>
      <p className="text-sm text-gray-700 font-medium">{title}</p>
      {period && (
        <p className="text-xs text-gray-500 mt-1">{period}</p>
      )}
    </motion.div>
  );

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Unable to load dashboard</h3>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={fetchDashboardData}
            className="bg-[#1F11ED] text-white px-6 py-2 rounded-lg hover:bg-[#1709C7] transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (loading || !dashboardData) {
    return (
      <div className="space-y-4">
        <div className="h-32 bg-gray-200 rounded-xl animate-pulse"></div>
        <div className="grid grid-cols-2 gap-4">
          <div className="h-24 bg-gray-200 rounded-xl animate-pulse"></div>
          <div className="h-24 bg-gray-200 rounded-xl animate-pulse"></div>
        </div>
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-16 bg-gray-200 rounded-xl animate-pulse"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Email Verification Banner - Priority 1 */}
      <EmailVerificationBanner user={user} />
      
      {/* Stripe Connect Banner - Priority 2 (only shows after email verified) */}
      {user?.emailVerified && <StripeConnectBanner user={user} />}

      {/* Two-Factor Reminder Banner - Priority 3 */}
      {user?.emailVerified && <TwoFactorReminderBanner user={user} />}
      
      {/* Welcome Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center py-4"
      >
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Welcome back, {user?.firstName || 'there'}!</h1>
        <p className="text-gray-600">Here's your credit control overview</p>
      </motion.div>

      {/* Stats Grid - Now at the top */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="space-y-4"
      >
        <h2 className="text-lg font-semibold text-gray-900">Dashboard Metrics</h2>
        <div className="grid grid-cols-2 gap-4">
          <StatCard
            title="Total Collected"
            value={`£${(dashboardData.kpis?.totalValueCollected || 0).toFixed(2)}`}
            icon={PoundSterling}
            color="green"
            period="All time"
            onClick={() => navigate('/portal/payments')}
          />
          <StatCard
            title="Approved Invoices"
            value={dashboardData.kpis?.approvedInvoices || 0}
            icon={CheckCircle}
            color="blue"
            period="Current"
            onClick={() => navigate('/portal/invoices')}
          />
          <StatCard
            title="Rejected Invoices"
            value={dashboardData.kpis?.rejectedInvoices || 0}
            icon={AlertCircle}
            color="red"
            period="Current"
            onClick={() => navigate('/portal/invoices')}
          />
          <StatCard
            title="Next Payment Due"
            value={dashboardData.kpis?.nextPaymentDue && (dashboardData.kpis.nextPaymentDue.date || dashboardData.kpis.nextPaymentDue.dueDate) ? `£${parseFloat(dashboardData.kpis.nextPaymentDue.amount || 0).toFixed(2)}` : '—'}
            icon={Clock}
            color="yellow"
            period={dashboardData.kpis?.nextPaymentDue && (dashboardData.kpis.nextPaymentDue.date || dashboardData.kpis.nextPaymentDue.dueDate) ? new Date(dashboardData.kpis.nextPaymentDue.date || dashboardData.kpis.nextPaymentDue.dueDate).toLocaleDateString('en-GB', {
              day: 'numeric',
              month: 'short',
              year: 'numeric'
            }) : 'All caught up'}
            onClick={() => navigate('/portal/invoices/received')}
          />
        </div>
      </motion.div>

      {/* Recent Activity */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="space-y-4"
      >
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">Recent Activity</h2>
          <Link to="/portal/invoices" className="text-sm text-[#1F11ED] font-medium">
            View All
          </Link>
        </div>
        
        <div className="space-y-3">
          {(dashboardData.activity?.recentActivity || []).slice(0, 4).map((activity, index) => {
            // Define activity type configurations with new color + icon system
            const activityConfig = {
              invoice_sent: {
                icon: '📤',
                bgColor: 'bg-blue-100',
                iconColor: 'text-blue-600',
                label: 'Sent'
              },
              invoice_approved: {
                icon: '⏳',
                bgColor: 'bg-yellow-100',
                iconColor: 'text-yellow-600',
                label: 'Approved'
              },
              invoice_rejected: {
                icon: '❌',
                bgColor: 'bg-red-100',
                iconColor: 'text-red-600',
                label: 'Rejected'
              },
              payment_received: {
                icon: '💰', // Money in icon
                bgColor: 'bg-green-100',
                iconColor: 'text-green-600',
                label: 'Earned'
              },
              payment_made: {
                icon: '💸', // Money out icon
                bgColor: 'bg-red-100',
                iconColor: 'text-red-600',
                label: 'Paid'
              },
              payment_failed: {
                icon: '⚠️',
                bgColor: 'bg-orange-100',
                iconColor: 'text-orange-600',
                label: 'Failed'
              },
              pending_approval: {
                icon: '⏳',
                bgColor: 'bg-yellow-100',
                iconColor: 'text-yellow-600',
                label: 'Pending'
              }
            };

            const config = activityConfig[activity.type] || activityConfig.invoice_sent;

            return (
              <motion.div
                key={activity.invoiceNumber || index}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.4 + index * 0.1 }}
                className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm"
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center space-x-3">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${config.bgColor}`}>
                      <span className="text-lg">{config.icon}</span>
                    </div>
                    <div>
                      <h3 className="font-medium text-gray-900">{activity.invoiceNumber || 'N/A'}</h3>
                      <p className="text-sm text-gray-600 capitalize">{activity.type?.replace('_', ' ') || 'Activity'}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-gray-900">£{(parseFloat(activity.amount) || 0).toFixed(2)}</p>
                    <p className="text-xs text-gray-500">{new Date(activity.date).toLocaleDateString()}</p>
                  </div>
                </div>
                <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100">
                  <div className="flex items-center space-x-2">
                    <span className="text-xs text-gray-500">{new Date(activity.date).toLocaleDateString()}</span>
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                      config.label === 'Earned' ? 'bg-green-100 text-green-700' :
                      config.label === 'Paid' ? 'bg-red-100 text-red-700' :
                      config.label === 'Pending' ? 'bg-yellow-100 text-yellow-700' :
                      'bg-gray-100 text-gray-700'
                    }`}>
                      {config.label}
                    </span>
                  </div>
                  <button
                    onClick={() => handleActivityNavigation(activity)}
                    className="bg-[#1F11ED] text-white px-3 py-1.5 rounded-lg text-xs font-medium hover:bg-blue-700 active:scale-95 transition-all duration-200"
                  >
                    View Details
                  </button>
                </div>
              </motion.div>
            );
          })}
        </div>
      </motion.div>

      {/* Quick Actions */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="space-y-3"
      >
        <h2 className="text-lg font-semibold text-gray-900">Quick Actions</h2>
        <div className="grid grid-cols-1 gap-3">
          <QuickActionCard
            icon={Plus}
            title="Send Invoice"
            description="Create and send a new invoice"
            href="/portal/send-invoice"
            color="green"
          />
          <QuickActionCard
            icon={FileText}
            title="View Invoices"
            description="Manage your sent and received invoices"
            href="/portal/invoices"
            color="blue"
          />
          <QuickActionCard
            icon={MessageSquare}
            title="Chat"
            description="Communicate about invoices"
            href="/portal/chat"
            color="purple"
          />
        </div>
      </motion.div>

      {/* Upcoming Payments */}
      {dashboardData.upcomingPayments?.upcomingPayments && dashboardData.upcomingPayments.upcomingPayments.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="space-y-4"
        >
          <h2 className="text-lg font-semibold text-gray-900">Upcoming Payments</h2>
          <div className="space-y-3">
            {dashboardData.upcomingPayments.upcomingPayments.slice(0, 2).map((payment, index) => (
              <motion.div
                key={payment.invoiceNumber || index}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.6 + index * 0.1 }}
                className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                      <Calendar className="w-4 h-4 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="font-medium text-gray-900">{payment.invoiceNumber}</h3>
                      <p className="text-sm text-gray-600">Invoice</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-gray-900">£{(parseFloat(payment.amount) || 0).toFixed(2)}</p>
                    <p className="text-xs text-gray-500">
                      Due {new Date(payment.dueDate).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      )}

      {/* Invoice Preview Modal */}
      {showInvoicePreview && selectedInvoice && (
        <MobileInvoicePreview
          invoice={selectedInvoice}
          isOpen={showInvoicePreview}
          onClose={() => {
            setShowInvoicePreview(false);
            setSelectedInvoice(null);
          }}
          onDownloadPDF={async () => {
            if (!selectedInvoice) return;
            try {
              await downloadInvoice(selectedInvoice, 'pdf', { forcePrintablePreview: true });
            } catch (error) {
              console.error('Error downloading invoice:', error);
              alert(error.response?.data?.error || error.message || 'Failed to download invoice. Please try again.');
            }
          }}
          showDownloadButtons={true}
          title="Invoice Preview"
        />
      )}
    </div>
  );
};

export default MobileClientDashboard;
